import React, {useEffect} from 'react';
  import {useApi} from "../../../providers/ApiProvider";
  import BusinessesPostsModel from "../../../models/BusinessesPostsModel";
  import BusinessesModel from "../../../models/BusinessesModel";
  import {useParams, Link, useNavigate} from "react-router-dom";
  import {useBreadcrumbs} from "../../../providers/BreadcrumbsProvider";
  import BusinessViewHeader from "../BusinessViewHeader";
  import BusinessViewMenu from "../BusinessViewMenu";
  import {useTranslation} from "react-i18next";
  import {useTranslation as useMyTranslation} from "../../../providers/TranslationProvider";
  import ButtonWithLoading from "../../layout/ui/ButtonWithLoading";

  function BusinessesPostTemplates() {
      const {language} = useMyTranslation();
      const {t} = useTranslation();
      const params = useParams();
      const navigate = useNavigate();
      const apiContext = useApi();
      const bpModel = new BusinessesPostsModel(apiContext.api);
      const businessesModel = new BusinessesModel(apiContext.api);
      const breadcrumbs = useBreadcrumbs();

      const [templates, setTemplates] = React.useState(null);
      const [loading, setLoading] = React.useState(true);
      const [generating, setGenerating] = React.useState(false);
      const [error, setError] = React.useState(null);
      const [copiedIdx, setCopiedIdx] = React.useState(null);

      useEffect(() => {
          businessesModel.view(params.locationId).then((response) => {
              if (response !== false) {
                  breadcrumbs.update({
                      title: response.data.title,
                      breadcrumbs: [
                          {label: t('Businesses List'), url: '/businesses'},
                          {label: response.data.title, url: '/businesses/' + params.locationId},
                          {label: t('Post-Vorlagen')}
                      ]
                  });
                  document.title = t('Post-Vorlagen') + ' | ' + response.data.title + ' | LocalBoost';
              }
          });
          loadSavedTemplates();
      }, [language]);

      const loadSavedTemplates = () => {
          setLoading(true);
          bpModel.getTemplates(params.locationId).then((response) => {
              if (response !== false && response.data) {
                  setTemplates(response.data);
              }
          }).finally(() => setLoading(false));
      };

      const generateTemplates = () => {
          setGenerating(true);
          setError(null);
          bpModel.generateTemplates(params.locationId, (err) => {
              const msg = (err && (err.error || err.message)) || '';
              setError(msg || t('Fehler beim Generieren der Vorlagen.'));
          }).then((response) => {
              if (response !== false && Array.isArray(response)) {
                  setTemplates(response);
              }
          }).finally(() => setGenerating(false));
      };

      const getIconClass = (icon) => {
          const map = {gift: 'ti-gift', sun: 'ti-sun', bulb: 'ti-bulb', star: 'ti-star', users: 'ti-users', discount: 'ti-discount-2'};
          return map[icon] || 'ti-article';
      };

      const getCategoryColor = (idx) => {
          const colors = ['primary', 'success', 'warning', 'info', 'secondary', 'danger'];
          return colors[idx % colors.length];
      };

      const copyToClipboard = (text, idx) => {
          navigator.clipboard.writeText(text).then(() => {
              setCopiedIdx(idx);
              setTimeout(() => setCopiedIdx(null), 2000);
          });
      };

      const applyTemplate = (template) => {
          sessionStorage.setItem('lb_template_text', template.summary || '');
          sessionStorage.setItem('lb_template_title', template.title || '');
          navigate('/businesses/' + params.locationId + '/publications/create');
      };

      return (
          <>
              <BusinessViewHeader locationId={params.locationId} onProfileRefresh={() => {}} />
              <BusinessViewMenu locationId={params.locationId} active="publications" />

              {error && (
                  <div className="alert alert-danger mt-3 d-flex align-items-center">
                      <i className="ti ti-alert-circle me-2 fs-5"></i><div>{error}</div>
                  </div>
              )}

              {loading && (
                  <div className="card rounded-2 mt-3"><div className="card-body p-5 text-center">
                      <div className="spinner-border text-primary"></div>
                      <p className="mt-2 text-muted">{t('Daten werden geladen...')}</p>
                  </div></div>
              )}

              {!loading && !templates && !generating && (
                  <div className="card rounded-2 mt-3"><div className="card-body p-5 text-center">
                      <i className="ti ti-template fs-1 d-block mb-3 text-primary" style={{fontSize: '48px'}}></i>
                      <h5>{t('KI-Post-Vorlagen')}</h5>
                      <p className="text-muted mb-4">{t('Lassen Sie sich branchenspezifische Post-Vorlagen generieren, abgestimmt auf Saison, Feiertage und Ihre Branche.')}</p>
                      <div className="d-flex justify-content-center">
                          <ButtonWithLoading className="btn btn-primary btn-lg" noOriginalIconClassName={true} iconClassName="ti ti-sparkles me-2" label={t('Vorlagen generieren')} loading={generating} onClick={generateTemplates} />
                      </div>
                  </div></div>
              )}

              {generating && (
                  <div className="card rounded-2 mt-3"><div className="card-body p-5 text-center">
                      <div className="spinner-border text-primary mb-3" style={{width: '3rem', height: '3rem'}}></div>
                      <h5>{t('Vorlagen werden generiert...')}</h5>
                      <p className="text-muted">{t('KI erstellt branchenspezifische Post-Vorlagen.')}</p>
                  </div></div>
              )}

              {templates && !generating && (
                  <>
                      <div className="d-flex justify-content-between align-items-center mt-3 mb-2">
                          <h5 className="mb-0"><i className="ti ti-template me-2"></i>{t('KI-Post-Vorlagen')}</h5>
                          <div className="d-flex gap-2">
                              <Link to={'/businesses/' + params.locationId + '/publications'} className="btn btn-sm btn-outline-secondary">
                                  <i className="ti ti-arrow-left me-1"></i>{t('Zur\u00fcck')}
                              </Link>
                              <ButtonWithLoading className="btn btn-sm btn-outline-primary" noOriginalIconClassName={true} iconClassName="ti ti-refresh me-1" label={t('Neu generieren')} loading={generating} onClick={generateTemplates} />
                          </div>
                      </div>

                      <div className="row">
                          {templates.map((tpl, idx) => {
                              const color = getCategoryColor(idx);
                              return (
                                  <div key={idx} className="col-lg-4 col-md-6 mb-3">
                                      <div className={'card rounded-2 h-100 border-' + color}>
                                          <div className={'card-header bg-light-' + color + ' py-3 border-bottom-0'}>
                                              <div className="d-flex align-items-center gap-2">
                                                  <i className={'ti ' + getIconClass(tpl.icon) + ' fs-5 text-' + color}></i>
                                                  <h6 className={'mb-0 fw-semibold text-' + color}>{tpl.title}</h6>
                                              </div>
                                              <span className="badge bg-light text-muted mt-1 fs-1">{tpl.category}</span>
                                          </div>
                                          <div className="card-body p-3">
                                              <p className="fs-3 mb-0" style={{whiteSpace: 'pre-wrap'}}>{tpl.summary}</p>
                                          </div>
                                          <div className="card-footer bg-transparent border-top d-flex gap-2 justify-content-end py-2">
                                              <button className="btn btn-sm btn-outline-secondary" onClick={() => copyToClipboard(tpl.summary, idx)}>
                                                  <i className={'ti me-1 ' + (copiedIdx === idx ? 'ti-check' : 'ti-copy')}></i>
                                                  {copiedIdx === idx ? t('Kopiert!') : t('Kopieren')}
                                              </button>
                                              <button className="btn btn-sm btn-primary" onClick={() => applyTemplate(tpl)}>
                                                  <i className="ti ti-pencil me-1"></i>{t('Verwenden')}
                                              </button>
                                          </div>
                                      </div>
                                  </div>
                              );
                          })}
                      </div>
                  </>
              )}
          </>
      );
  }

  export default BusinessesPostTemplates;
  